import Header from '../layouts/Header';
import React, { useState, useEffect } from 'react';
import {
  Container, Button, ButtonGroup, Form, Table, Modal, Alert, Row, Col, Spinner, Card, Badge
} from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const DormitoryAllocation = () => {
  const [villages, setVillages] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showStudentSearchModal, setShowStudentSearchModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutTarget, setCheckoutTarget] = useState(null); // { alloc_id, st_name }
  const [checkoutDate, setCheckoutDate] = useState(toISODate(new Date()));
  const [checkoutReason, setCheckoutReason] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [genderFilter, setGenderFilter] = useState('all');

  // เพิ่มที่นอนเสริมให้หลังที่เต็ม — นักเรียนยอมอยู่รวมกันแน่นขึ้น
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityTarget, setCapacityTarget] = useState(null); // building ที่กำลังเพิ่มที่ว่าง
  const [capacityAdd, setCapacityAdd] = useState(1);
  const [capacityReason, setCapacityReason] = useState('');
  const [capacityLoading, setCapacityLoading] = useState(false);

  const [allocateData, setAllocateData] = useState({
    tb_dormitory_building_dorm_id: '',
    tb_student_st_id: '',
    checkin_date: toISODate(new Date()),
    alloc_note: ''
  });

  // Fetch villages
  const fetchVillages = async () => {
    try {
      const response = await axiosInstance.get('/api/dormitory/villages');
      if (response.data.success) {
        const activeVillages = response.data.data.filter((village) => village.village_status === 'active');
        setVillages(activeVillages);

        if (activeVillages.length === 0) {
          setSelectedVillage(null);
          return;
        }

        setSelectedVillage((prev) => {
          if (prev && activeVillages.some((village) => String(village.village_id) === String(prev))) {
            return prev;
          }
          return activeVillages[0].village_id;
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลหมู่หอพักไม่สำเร็จ');
    }
  };

  // Fetch buildings for selected village
  const fetchBuildings = async () => {
    if (!selectedVillage) {
      setBuildings([]);
      setAllocateData((prev) => ({
        ...prev,
        tb_dormitory_building_dorm_id: ''
      }));
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/dormitory/villages/${selectedVillage}/buildings`);
      if (response.data.success) {
        const activeBuildings = response.data.data.filter((building) => building.dorm_status === 'active');
        setBuildings(activeBuildings);

        setAllocateData((prev) => {
          const stillValid = activeBuildings.some(
            (building) => String(building.dorm_id) === String(prev.tb_dormitory_building_dorm_id)
          );

          return {
            ...prev,
            tb_dormitory_building_dorm_id: stillValid
              ? prev.tb_dormitory_building_dorm_id
              : (activeBuildings[0]?.dorm_id || '')
          };
        });
      }
    } catch (err) {
      console.error('Failed to load buildings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVillages();
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, [selectedVillage]);

  const handleVillageChange = (e) => {
    setSelectedVillage(e.target.value);
  };

  const handleSearchStudent = async (e) => {
    const key = e.target.value;
    setStudentSearch(key);

    if (key.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axiosInstance.get(`/api/dormitory/students/search?searchKey=${key}`);
      if (response.data.success) {
        setSearchResults(response.data.data);
      }
    } catch (err) {
      console.error('Failed to search students:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setAllocateData(prev => ({
      ...prev,
      tb_student_st_id: student.st_id
    }));
    setStudentSearch('');
    setSearchResults([]);
    setShowStudentSearchModal(false);
  };

  const handleAllocateChange = (e) => {
    const { name, value } = e.target;
    setAllocateData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShowAllocateModal = () => {
    setSelectedStudent(null);
    setAllocateData({
      tb_dormitory_building_dorm_id: buildings.length > 0 ? buildings[0].dorm_id : '',
      tb_student_st_id: '',
      checkin_date: toISODate(new Date()),
      alloc_note: ''
    });
    setShowAllocateModal(true);
  };

  const handleOpenAllocateForBuilding = (building) => {
    setSelectedStudent(null);
    setAllocateData({
      tb_dormitory_building_dorm_id: building.dorm_id,
      tb_student_st_id: '',
      checkin_date: toISODate(new Date()),
      alloc_note: ''
    });
    setShowAllocateModal(true);
  };

  const handleOpenCapacity = (e, building) => {
    e.stopPropagation();
    setCapacityTarget(building);
    setCapacityAdd(1);
    setCapacityReason('');
    setShowCapacityModal(true);
  };

  const handleAddCapacity = async () => {
    if (!capacityTarget) return;

    const add = parseInt(capacityAdd, 10);
    if (!add || add < 1) {
      setError('กรุณาระบุจำนวนที่ว่างที่ต้องการเพิ่ม');
      return;
    }
    if (!capacityReason.trim()) {
      setError('กรุณาระบุเหตุผลที่เพิ่มที่ว่าง');
      return;
    }

    setCapacityLoading(true);
    try {
      setError('');
      const res = await axiosInstance.post(
        `/api/dormitory/buildings/${capacityTarget.dorm_id}/capacity`,
        { add, reason: capacityReason.trim() }
      );
      if (res.data.success) {
        setShowCapacityModal(false);
        setCapacityTarget(null);
        setSuccess(res.data.message || 'เพิ่มที่ว่างเรียบร้อยแล้ว');
        await fetchBuildings();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เพิ่มที่ว่างไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      setShowCapacityModal(false);
    } finally {
      setCapacityLoading(false);
    }
  };

  const handleOpenCheckout = (e, student) => {
    e.stopPropagation();
    setCheckoutTarget({ alloc_id: student.alloc_id, st_name: student.st_name });
    setCheckoutDate(toISODate(new Date()));
    setCheckoutReason('');
    setShowCheckoutModal(true);
  };

  const handleCheckout = async () => {
    if (!checkoutTarget) return;
    setCheckoutLoading(true);
    try {
      const res = await axiosInstance.post('/api/dormitory/checkout', {
        alloc_id: checkoutTarget.alloc_id,
        checkout_date: checkoutDate,
        reason: checkoutReason
      });
      if (res.data.success) {
        setShowCheckoutModal(false);
        setCheckoutTarget(null);
        setSuccess(`ย้าย ${checkoutTarget.st_name} ออกจากหอพักเรียบร้อยแล้ว`);
        await fetchBuildings();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'ย้ายออกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      setShowCheckoutModal(false);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleAllocate = async () => {
    try {
      setError('');
      setSuccess('');

      if (!allocateData.tb_dormitory_building_dorm_id || !allocateData.tb_student_st_id) {
        setError('กรุณาเลือกทั้งอาคารและนักเรียน');
        return;
      }

      const response = await axiosInstance.post('/api/dormitory/allocate', allocateData);

      if (response.data.success) {
        setSuccess(`จัดสรร ${selectedStudent?.st_name} เข้าหอพักเรียบร้อยแล้ว`);
        setShowAllocateModal(false);
        setSelectedStudent(null);
        await fetchBuildings();
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'จัดสรรนักเรียนเข้าหอพักไม่สำเร็จ';
      const code = err.response?.data?.code;

      if (code === 'GENDER_MISMATCH') {
        setError(`เพศของนักเรียน (${selectedStudent?.st_gen === 'm' ? 'ชาย' : 'หญิง'}) ไม่ตรงกับหมู่หอพักนี้`);
      } else if (code === 'DORM_FULL') {
        setError('อาคารหอพักนี้เต็มแล้ว กรุณาเลือกอาคารอื่น');
      } else if (code === 'STUDENT_ALREADY_ALLOCATED') {
        setError('นักเรียนคนนี้มีข้อมูลเข้าพักอยู่แล้ว');
      } else if (code === 'INACTIVE_STUDENT') {
        setError('ไม่พบนักเรียนในระบบ หรือสถานะนักเรียนไม่สามารถเข้าพักได้');
      } else {
        setError(errorMsg);
      }
    }
  };

  const filteredVillages = villages.filter(v =>
    genderFilter === 'all' || v.village_gender === genderFilter
  );

  const handleGenderFilterChange = (value) => {
    setGenderFilter(value);
    const filtered = villages.filter(v => value === 'all' || v.village_gender === value);
    if (filtered.length > 0) {
      setSelectedVillage(filtered[0].village_id);
    } else {
      setSelectedVillage(null);
    }
  };

  const selectedBuildingName = buildings.find(b => b.dorm_id == allocateData.tb_dormitory_building_dorm_id)?.dorm_name || '';
  const selectedVillageName = villages.find(v => v.village_id == selectedVillage)?.village_name || '';

  const genderBadge = (gender) => {
    if (gender === 'm') return <Badge bg="primary">👨 ชาย</Badge>;
    return <Badge bg="danger">👩 หญิง</Badge>;
  };

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>จัดสรรนักเรียนเข้าหอพัก</h2>
        <Button variant="success" onClick={handleShowAllocateModal}>
          + จัดสรรนักเรียนเข้าหอพัก
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {/* Village Selector */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Label><strong>ประเภทหอพัก</strong></Form.Label>
              <div>
                <ButtonGroup>
                  <Button variant={genderFilter === 'all' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('all')}>ทั้งหมด</Button>
                  <Button variant={genderFilter === 'm' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('m')}>👨 ชาย</Button>
                  <Button variant={genderFilter === 'f' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('f')}>👩 หญิง</Button>
                </ButtonGroup>
              </div>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label><strong>เลือกเขต/หมู่หอพัก</strong></Form.Label>
                <Form.Select
                  value={selectedVillage || ''}
                  onChange={handleVillageChange}
                  disabled={filteredVillages.length === 0}
                >
                  {filteredVillages.length === 0 ? (
                    <option value="">ไม่มีหมู่หอพัก</option>
                  ) : (
                    filteredVillages.map(v => (
                      <option key={v.village_id} value={v.village_id}>
                        {v.village_name} ({v.village_gender === 'm' ? '👨 ชาย' : '👩 หญิง'})
                      </option>
                    ))
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <div className="pb-1">
                <strong>เขต/หมู่หอพักปัจจุบัน:</strong> {selectedVillageName || '-'}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Village & Building Info */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>หมู่หอพักที่เลือก:</strong> {selectedVillageName || '-'}</p>
            </Col>
            <Col md={6}>
              <p><strong>จำนวนหอพักทั้งหมด:</strong> {buildings.length}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Available Buildings */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">หอพักที่มีที่ว่างใน {selectedVillageName}</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : buildings.length === 0 ? (
            <p className="text-muted mb-0">ไม่มีอาคารที่พร้อมใช้งานในหมู่หอพักนี้</p>
          ) : (
            <Row>
              {buildings.map(building => {
                const occupied = building.current_occupancy;
                const capacity = building.capacity_total;
                const vacant = capacity - occupied;
                const isFull = occupied >= capacity;

                return (
                  <Col md={6} lg={3} key={building.dorm_id} className="mb-3">
                    <Card
                      className={`h-100 ${isFull ? 'border-danger' : 'border-success'}`}
                      style={!isFull ? { cursor: 'pointer' } : {}}
                      onClick={!isFull ? () => handleOpenAllocateForBuilding(building) : undefined}
                    >
                      <Card.Body>
                        <h6 className="card-title">{building.dorm_name}</h6>
                        <p className="text-muted small mb-2">{building.dorm_code}</p>
                        <div className="mb-3">
                          <div className="progress">
                            <div
                              className={`progress-bar bg-${isFull ? 'danger' : occupied > 0.8 * capacity ? 'warning' : 'success'}`}
                              style={{ width: `${Math.round((occupied / capacity) * 100)}%` }}
                            >
                              {Math.round((occupied / capacity) * 100)}%
                            </div>
                          </div>
                        </div>
                        <small>
                          เข้าพักแล้ว <strong>{occupied}/{capacity}</strong>, ว่าง {vacant} ที่
                        </small>
                        {isFull && (
                          <Badge bg="danger" className="ms-2">เต็ม</Badge>
                        )}

                        <hr className="my-3" />
                        {!isFull ? (
                          <div className="d-grid mb-2">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleOpenAllocateForBuilding(building); }}
                            >
                              + จัดสรรนักเรียน
                            </Button>
                          </div>
                        ) : (
                          // หลังเต็มแล้วแต่ยังรับเพิ่มได้ถ้านักเรียนยอมอยู่รวมกันแน่นขึ้น
                          <div className="d-grid mb-2">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={(e) => handleOpenCapacity(e, building)}
                            >
                              + เพิ่มที่ว่าง
                            </Button>
                          </div>
                        )}

                        <div>
                          <small className="text-muted d-block mb-2">รายชื่อนักเรียนที่พักอยู่</small>
                          {building.residents && building.residents.length > 0 ? (
                            building.residents.map((student, index) => (
                              <div key={`${building.dorm_id}-${student.st_idnumber}-${index}`} className="d-flex align-items-center gap-1 mb-1">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="py-0 px-1"
                                  style={{ fontSize: '0.65rem', lineHeight: '1.4' }}
                                  onClick={(e) => handleOpenCheckout(e, student)}
                                  title="ย้ายออก"
                                >
                                  ย้ายออก
                                </Button>
                                <small>
                                  {index + 1}. {student.st_idnumber} {student.st_name} {student.tb_class_c_id && student.r_no ? `(ม. ${student.tb_class_c_id}/${student.r_no})` : ''}
                                </small>
                              </div>
                            ))
                          ) : (
                            <small className="text-muted d-block">- ไม่มีนักเรียนพักอยู่ -</small>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Modal: Allocate Student */}
      <Modal show={showAllocateModal} onHide={() => setShowAllocateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>จัดสรรนักเรียนเข้าหอพัก</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Village Selector */}
            <Form.Group className="mb-3">
              <Form.Label><strong>หมู่หอพัก</strong></Form.Label>
              <Form.Select
                value={selectedVillage}
                onChange={handleVillageChange}
              >
                {villages.map(v => (
                  <option key={v.village_id} value={v.village_id}>
                    {v.village_name} ({v.village_gender === 'm' ? '👨 ชาย' : '👩 หญิง'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Building Selector */}
            <Form.Group className="mb-3">
              <Form.Label><strong>หอพัก</strong></Form.Label>
              <Form.Select
                name="tb_dormitory_building_dorm_id"
                value={allocateData.tb_dormitory_building_dorm_id}
                onChange={handleAllocateChange}
              >
                {buildings.map(b => {
                  const occupied = b.current_occupancy;
                  const capacity = b.capacity_total;
                  const isFull = occupied >= capacity;
                  return (
                    <option 
                      key={b.dorm_id} 
                      value={b.dorm_id}
                      disabled={isFull}
                    >
                      {b.dorm_name} ({occupied}/{capacity}) {isFull ? '- เต็ม' : '- ว่าง'}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>

            {/* Student Selector */}
            <Form.Group className="mb-3">
              <Form.Label><strong>นักเรียน</strong></Form.Label>
              <div className="input-group">
                <Form.Control
                  type="text"
                  placeholder="คลิกเพื่อค้นหานักเรียนด้วยเลขประจำตัวหรือชื่อ..."
                  value={selectedStudent ? `${selectedStudent.st_idnumber} - ${selectedStudent.st_name}` : ''}
                  readOnly
                  onClick={() => setShowStudentSearchModal(true)}
                  className="cursor-pointer"
                  style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                />
                {selectedStudent && (
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      setSelectedStudent(null);
                      setAllocateData(prev => ({ ...prev, tb_student_st_id: '' }));
                    }}
                  >
                    ล้าง
                  </Button>
                )}
              </div>
              {selectedStudent && (
                <small className="text-muted d-block mt-2">
                  เพศ: {genderBadge(selectedStudent.st_gen)} | สถานะ: {selectedStudent.st_status === '1' ? '✓ ใช้งาน' : '✗ ไม่พร้อมใช้งาน'}
                </small>
              )}
            </Form.Group>

            {/* Checkin Date */}
            <Form.Group className="mb-3">
              <Form.Label>วันที่เข้าพัก</Form.Label>
              <ThaiDateInput
                name="checkin_date"
                value={allocateData.checkin_date}
                onChange={handleAllocateChange}
              />
            </Form.Group>

            {/* Notes */}
            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                as="textarea"
                name="alloc_note"
                value={allocateData.alloc_note}
                onChange={handleAllocateChange}
                rows={3}
                placeholder="รายละเอียดเพิ่มเติม..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAllocateModal(false)}>
            ยกเลิก
          </Button>
          <Button 
            variant="success" 
            onClick={handleAllocate}
            disabled={!selectedStudent || !allocateData.tb_dormitory_building_dorm_id}
          >
            ยืนยันการจัดสรร
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Student Search */}
      <Modal show={showStudentSearchModal} onHide={() => setShowStudentSearchModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>ค้นหานักเรียน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>ค้นหาจากเลขประจำตัวหรือชื่อ</Form.Label>
            <Form.Control
              type="text"
              value={studentSearch}
              onChange={handleSearchStudent}
              placeholder="กรอกเลขประจำตัวหรือชื่อนักเรียน..."
              autoFocus
            />
            <Form.Text className="text-muted">พิมพ์อย่างน้อย 3 ตัวอักษรเพื่อค้นหา</Form.Text>
          </Form.Group>

          {searchLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {searchResults.length > 0 && (
            <Table responsive striped bordered hover size="sm">
              <thead className="table-light">
                <tr>
                  <th>เลขประจำตัว</th>
                  <th>ชื่อ</th>
                  <th>เพศ</th>
                  <th>สถานะ</th>
                  <th>เลือก</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(student => (
                  <tr key={student.st_id}>
                    <td>{student.st_idnumber}</td>
                    <td>{student.st_name}</td>
                    <td>{genderBadge(student.st_gen)}</td>
                    <td>
                      {student.st_status === '1' || student.st_status === '3' ? (
                        <Badge bg="success">✓ ใช้งาน</Badge>
                      ) : (
                        <Badge bg="danger">✗ ไม่พร้อมใช้งาน</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleSelectStudent(student)}
                        disabled={student.st_status === '0' || student.st_status === '2'}
                      >
                        เลือก
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {studentSearch.length >= 3 && searchResults.length === 0 && !searchLoading && (
            <Alert variant="info">ไม่พบนักเรียนที่พร้อมใช้งานตามคำค้นหา</Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal: Add Capacity */}
      <Modal show={showCapacityModal} onHide={() => setShowCapacityModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>เพิ่มที่ว่างให้ {capacityTarget?.dorm_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            ตอนนี้เข้าพักแล้ว <strong>{capacityTarget?.current_occupancy}</strong> คน
            จากความจุ <strong>{capacityTarget?.capacity_total}</strong> ที่
          </p>
          <Form.Group className="mb-3">
            <Form.Label>เพิ่มอีกกี่ที่</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={20}
              value={capacityAdd}
              onChange={(e) => setCapacityAdd(e.target.value)}
            />
            <Form.Text className="text-muted">
              ความจุใหม่จะเป็น {(Number(capacityTarget?.capacity_total) || 0) + (parseInt(capacityAdd, 10) || 0)} ที่ (เพิ่มได้ครั้งละไม่เกิน 20 ที่)
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>เหตุผล <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={capacityReason}
              onChange={(e) => setCapacityReason(e.target.value)}
              placeholder="เช่น นักเรียนยินยอมอยู่ห้องละ 6 คน, เสริมที่นอนชั่วคราวภาคเรียนนี้..."
            />
            <Form.Text className="text-muted">
              บันทึกไว้ในประวัติการแก้ไขข้อมูลพร้อมชื่อผู้บันทึก
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCapacityModal(false)} disabled={capacityLoading}>
            ยกเลิก
          </Button>
          <Button
            variant="warning"
            onClick={handleAddCapacity}
            disabled={capacityLoading || !capacityReason.trim() || !(parseInt(capacityAdd, 10) > 0)}
          >
            {capacityLoading ? <Spinner animation="border" size="sm" /> : 'ยืนยันเพิ่มที่ว่าง'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Checkout Confirm */}
      <Modal show={showCheckoutModal} onHide={() => setShowCheckoutModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันการย้ายออก</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>ต้องการย้าย <strong>{checkoutTarget?.st_name}</strong> ออกจากหอพักใช่หรือไม่?</p>
          <Form.Group className="mb-3">
            <Form.Label>วันที่ย้ายออก</Form.Label>
            <ThaiDateInput
              value={checkoutDate}
              onChange={v => setCheckoutDate(v)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>เหตุผล <small className="text-muted">(ไม่บังคับ)</small></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={checkoutReason}
              onChange={e => setCheckoutReason(e.target.value)}
              placeholder="ระบุเหตุผลการย้ายออก..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCheckoutModal(false)} disabled={checkoutLoading}>
            ยกเลิก
          </Button>
          <Button variant="danger" onClick={handleCheckout} disabled={checkoutLoading}>
            {checkoutLoading ? <Spinner animation="border" size="sm" /> : 'ยืนยันย้ายออก'}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryAllocation;
